"use client";

import { FC, HTMLAttributes, ReactNode } from "react";
import { clsx } from "clsx";

import CTASquareBox, { ICtaSquareBox } from "./CTASquareBox";
import HtmlText from "../common/HtmlText";
import { Element, Link } from "react-scroll";

// ------------------------------------------------------------------------------

export type IServiceTabListSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  ctaSquareBox?: ICtaSquareBox;
  tabList?: string[];
  tabContent?: {
    heading?: string;
    stages?: {
      heading?: string;
      description?: ReactNode;
    }[];
  }[];
};

const ServiceTabListSection: FC<IServiceTabListSectionProps> = (props) => {
  const { heading, ctaSquareBox, tabList, tabContent, className, ...other } =
    props;

  return (
    <section
      className={clsx("container py-5 lg:py-8 xl:py-[50px]", className)}
      {...other}
    >
      <div className="flex flex-col lg:flex-row">
        {heading && (
          <div className="w-full flex-[1_0_auto] border-t-2 border-t-primary-900 pt-5 lg:w-60 lg:pt-7 xl:w-[276px]">
            <h3 className="mb-5 text-base font-normal text-black-900 lg:text-base xl:text-h6">
              {heading}
            </h3>
          </div>
        )}

        <div className="mb-5 w-full border-t-2 border-t-[#D9D9D9] pt-5 md:mb-7 lg:mb-11 lg:pt-7 xl:mb-14">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:gap-7 lg:pl-6 xl:pl-8">
            {tabList &&
              tabList.map((heading) => (
                <Link
                  key={heading}
                  spy={true}
                  smooth={true}
                  to={heading}
                  offset={-60}
                  className="w-full cursor-pointer p-5 text-center text-lg font-normal text-black-900 odd:bg-primary-100 even:bg-secondary-50 lg:p-7 lg:text-h6 xl:text-h5"
                >
                  {heading}
                </Link>
              ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {ctaSquareBox && (
          <CTASquareBox
            className="h-fit"
            heading={ctaSquareBox.heading}
            description={ctaSquareBox.description}
            button={ctaSquareBox.button}
            variant="secondary"
          />
        )}

        <div>
          {tabContent && (
            <>
              {tabContent.map(({ heading, stages }) => (
                <div
                  key={heading}
                  className="mt-5 flex w-full flex-col first:mt-0 md:mb-0 md:mr-6 md:mt-8 lg:mt-11 xl:mr-10"
                >
                  {stages?.map(
                    (item, idx) =>
                      (item.heading || item.description) && (
                        <Element
                          name={idx === 0 ? (heading ?? "") : ""}
                          key={idx}
                          className="w-full border-t-2 border-t-[#D9D9D9] pb-5 first:mt-5 last:pb-0 md:pb-8 lg:pb-11 first:lg:mt-0"
                        >
                          <div
                            className={clsx(
                              "pt-5 md:flex lg:pt-7",
                              item.heading && "ml-3 md:ml-5 lg:ml-6 xl:ml-8",
                            )}
                          >
                            {item.heading && (
                              <div className="w-full flex-[1_0_auto] md:w-32 lg:w-36 xl:w-[173px]">
                                <h6 className="mb-4 text-base font-normal text-black-900 md:mb-0 lg:text-base xl:text-h6">
                                  {item.heading}
                                </h6>
                              </div>
                            )}
                            {item.description && (
                              <div className="ml-3 flex flex-col gap-2 md:gap-4 lg:ml-6 xl:ml-8">
                                <HtmlText className="prose-p:md:text-h6">
                                  {item.description}
                                </HtmlText>
                              </div>
                            )}
                          </div>
                        </Element>
                      ),
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServiceTabListSection;
